import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerOverlay,
    Flex,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    Textarea,
    VStack,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { brandCopy } from '../constants/brand';
import {
    TbArrowUp,
    TbPencil,
    TbExternalLink,
    TbPackage,
    TbChevronDown,
    TbEdit,
    TbMenu2,
    TbSearch,
    TbTrash,
} from 'react-icons/tb';
import { Icon as YaxleIcon, IconButton as YaxleIconButton } from '../ui';
import useAskCarlChat, { isApprovalRequest } from '../hooks/useAskCarlChat';
import CarlSidebar, { SIDEBAR_COLLAPSED_KEY, readStoredFlag, writeStoredFlag } from '../components/askCarl/CarlSidebar';
import CarlSearchModal from '../components/askCarl/CarlSearchModal';
import CarlMessageBody, { PendingInputCard } from '../components/askCarl/CarlMessageBody';
import CarlFeedbackControls from '../components/askCarl/CarlFeedbackControls';
import CarlLogo from '../components/askCarl/CarlLogo';
import CarlThinkingSparkle from '../components/askCarl/CarlThinkingSparkle';
import SessionContext from '../contexts/SessionContext';
import { isEmbedMode } from '../utils/embedMode';
import usePageTitle from '../hooks/usePageTitle';

const pulseGlow = keyframes`
    0%, 100% { 
        opacity: 0.4;
        transform: scale(1);
    }
    50% { 
        opacity: 0.8;
        transform: scale(1.05);
    }
`;

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`;

/** How long after a response settles before the "Was this helpful?" nudge shows. */
const FEEDBACK_NUDGE_DELAY_MS = 45_000;

const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
};

const ThinkingIndicator = () => (
    <HStack
        alignSelf="flex-start"
        spacing={3}
        animation={`${fadeIn} 0.3s ease-out`}
    >
        <CarlThinkingSparkle size="22px" />
        <Box
            bg="white"
            borderRadius="2xl"
            px={4}
            py={3}
            border="1px solid"
            borderColor="gray.200"
            boxShadow="none"
        >
            <HStack spacing={2}>
                <Box
                    h="6px"
                    w="6px"
                    borderRadius="full"
                    bg="#0071E3"
                    animation={`${pulseGlow} 1.4s ease-in-out infinite`}
                />
                <Box
                    h="6px"
                    w="6px"
                    borderRadius="full"
                    bg="#5A64A0"
                    animation={`${pulseGlow} 1.4s ease-in-out 0.2s infinite`}
                />
                <Box
                    h="6px"
                    w="6px"
                    borderRadius="full"
                    bg="#6B76B5"
                    animation={`${pulseGlow} 1.4s ease-in-out 0.4s infinite`}
                />
                <Text fontSize="sm" color="gray.500" ml={2}>
                    Carl is thinking...
                </Text>
            </HStack>
        </Box>
    </HStack>
);

const AskCarl = () => {
    const sessionContext = useContext(SessionContext);
    const location = useLocation();
    const navigate = useNavigate();
    usePageTitle('Ask Carl');
    const [input, setInput] = useState('');
    const endRef = useRef<HTMLDivElement>(null);
    /** Prompt handed off from the command search palette ("Ask Carl" mode). */
    const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
    const {
        messages,
        isLoading,
        sendMessage,
        pendingInputRequests,
        respondToInput,
        threads,
        activeThreadId,
        startNewThread,
        selectThread,
        deleteThread,
        renameThread,
        submitFeedback,
    } = useAskCarlChat();
    const activeThread = threads.find((thread) => thread.threadId === activeThreadId);

    const handleSelectThread = (threadId: string) => {
        selectThread(threadId);
    };

    const handleStartNewThread = () => {
        startNewThread();
    };

    /** Sidebar rail state (ENG-2169): collapse choice persists per browser. */
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => readStoredFlag(SIDEBAR_COLLAPSED_KEY));
    const toggleSidebarCollapsed = () => {
        setIsSidebarCollapsed((previous) => {
            writeStoredFlag(SIDEBAR_COLLAPSED_KEY, !previous);
            return !previous;
        });
    };
    /** Search-chats modal (ENG-2169), opened from the sidebar. */
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    /** Hover state for the collapsible "Order references" dropdown. */
    const [refsMenuOpen, setRefsMenuOpen] = useState(false);
    const commitTitleEdit = () => {
        setIsEditingTitle(false);
        if (activeThreadId && titleDraft.trim() && titleDraft.trim() !== activeThread?.title) {
            renameThread(activeThreadId, titleDraft);
        }
    };
    const userDisplayName = useMemo(() => {
        if (!sessionContext?.isAuthenticated) return 'there';
        const combined = `${sessionContext.firstName} ${sessionContext.lastName}`.trim();
        return combined.length ? combined : sessionContext.firstName;
    }, [sessionContext]);

    /**
     * Orders referenced across this thread (a thread can touch several).
     * Each entry keeps the message where that order first appeared so the
     * header timeline can jump back to it.
     */
    const referencedOrders = useMemo(() => {
        const seen = new Map<string, { orderId: string; roNumber?: string; vehicleName?: string; vin?: string; messageId: string }>();
        for (const message of messages) {
            const detail = message.ui?.orderDetail as Record<string, unknown> | undefined;
            const orderId = detail?.orderId as string | undefined;
            if (!orderId || seen.has(orderId)) continue;
            seen.set(orderId, {
                orderId,
                roNumber: detail?.roNumber as string | undefined,
                vehicleName: detail?.vehicleName as string | undefined,
                vin: detail?.vin as string | undefined,
                messageId: message.id,
            });
        }
        return Array.from(seen.values());
    }, [messages]);

    const scrollToMessage = (messageId: string) => {
        document.getElementById(`carl-message-${messageId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [messages.length, isLoading, pendingInputRequests.length]);

    // Prompt handed off from the command search palette: park it, start a
    // fresh thread, and clear the navigation state so refreshes don't resend.
    useEffect(() => {
        const prompt = (location.state as { askCarlPrompt?: string } | null)?.askCarlPrompt;
        if (!prompt?.trim()) return;
        setPendingPrompt(prompt.trim());
        // Keep the search string (e.g. ?embed=1) — replacing with the bare
        // pathname would drop the embed flag mid-session.
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
        handleStartNewThread();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    // Once the fresh (empty) thread is active, fire the parked prompt.
    useEffect(() => {
        if (!pendingPrompt || isLoading || messages.length > 0) return;
        setPendingPrompt(null);
        sendMessage(pendingPrompt);
    }, [pendingPrompt, activeThreadId, messages.length, isLoading, sendMessage]);

    // A recent thread opened from the command search palette: select it and
    // clear the navigation state so a refresh doesn't reselect.
    useEffect(() => {
        const threadId = (location.state as { askCarlThreadId?: string } | null)?.askCarlThreadId;
        if (!threadId) return;
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
        handleSelectThread(threadId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    // Nudge for feedback on the latest response after the thread sits idle
    // for a bit — continuing the conversation or rating cancels it.
    const [nudgedMessageId, setNudgedMessageId] = useState<string | null>(null);
    useEffect(() => {
        setNudgedMessageId(null);
        // Not done yet: streaming, or parked on an approval/question.
        if (isLoading || pendingInputRequests.length > 0) return undefined;
        const last = messages[messages.length - 1];
        if (!last || last.role !== 'assistant' || last.feedback || !last.content.trim()) return undefined;
        const timer = window.setTimeout(() => setNudgedMessageId(last.id), FEEDBACK_NUDGE_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [isLoading, messages, activeThreadId, pendingInputRequests.length]);

    /**
     * Embed mode (`?embed=1`, e.g. the partbay-mobile WebView): the page owns
     * the whole viewport — no desktop-shell margin/height compensation — and
     * gets a ChatGPT-style mobile header (hamburger → history drawer, centered
     * title, compose icon) plus a single-row auto-growing composer.
     */
    const isEmbedded = useMemo(
        () => isEmbedMode(location.search),
        [location.search],
    );

    /** Mobile history drawer (embed only). */
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [historyQuery, setHistoryQuery] = useState('');
    const filteredThreads = useMemo(() => {
        const query = historyQuery.trim().toLowerCase();
        const sorted = [...threads].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
        if (!query) return sorted;
        return sorted.filter((thread) => thread.title.toLowerCase().includes(query));
    }, [threads, historyQuery]);

    /**
     * Embed: keyboard overlap in px, measured from the visualViewport (the
     * only reliable keyboard signal inside a WKWebView). The layout shrinks
     * by this inset so the composer animates down to sit right above the
     * keys, ChatGPT-style — native-side KeyboardAvoidingView double-shifts.
     */
    const [keyboardInset, setKeyboardInset] = useState(0);
    useEffect(() => {
        if (!isEmbedded || typeof window === 'undefined') return undefined;
        const viewport = window.visualViewport;
        if (!viewport) return undefined;
        // Measure the keyboard from the page's OWN viewport: layout viewport
        // (window.innerHeight) minus the visual viewport gives exactly the area
        // the keyboard covers, with no double-counting (a native keyboard
        // height would over-shoot, since it isn't the same as the WebView's
        // viewport shrink).
        const update = () => {
            // WKWebView auto-scrolls the document to reveal the focused caret;
            // the layout is sized to the viewport (nothing should ever scroll
            // at the window level), so any drift here shoves the composer
            // off-screen — pin it back before measuring.
            if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
            const overlap = window.innerHeight - viewport.height - viewport.offsetTop;
            setKeyboardInset(Math.max(0, Math.round(overlap)));
        };
        // WKWebView doesn't reliably fire visualViewport resize while the
        // keyboard animates in/out — re-measure on a short burst after any
        // focus change so a missed event can't strand the composer behind
        // (or floating above) the keys.
        let burstTimers: number[] = [];
        const burst = () => {
            burstTimers.forEach((timer) => window.clearTimeout(timer));
            burstTimers = [50, 150, 300, 500, 800].map((ms) => window.setTimeout(update, ms));
        };
        update();
        viewport.addEventListener('resize', update);
        viewport.addEventListener('scroll', update);
        window.addEventListener('resize', update);
        window.addEventListener('focusin', burst);
        window.addEventListener('focusout', burst);
        return () => {
            viewport.removeEventListener('resize', update);
            viewport.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            window.removeEventListener('focusin', burst);
            window.removeEventListener('focusout', burst);
            burstTimers.forEach((timer) => window.clearTimeout(timer));
        };
    }, [isEmbedded]);
    /** Keyboard is up in the embed — compact the composer and pin it above the keys. */
    const keyboardOpen = isEmbedded && keyboardInset > 0;

    /** Auto-growing composer (embed only): height tracks content up to 200px. */
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    useEffect(() => {
        if (!isEmbedded) return;
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    }, [input, isEmbedded, keyboardOpen]);

    // No programmatic keyboard: iOS keyboard handling inside a WebView shifts
    // the page unpredictably, so the keyboard only appears when the user taps
    // the composer; the visualViewport inset above handles the rest.

    /*
     * The greeting collapses by animating max-height, which only starts moving
     * once max-height drops below the content's real height — a fixed ceiling
     * buys a dead zone at the head of the animation where the composer sits
     * still, then lurches. Measuring the content keeps the ceiling snug so the
     * collapse moves from the first frame.
     */
    const welcomeRef = useRef<HTMLDivElement>(null);
    const [welcomeHeight, setWelcomeHeight] = useState<number | null>(null);
    useEffect(() => {
        const el = welcomeRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        // Border box, not `contentRect` — the latter drops the block's own
        // padding, which would leave the ceiling short and clip the collapse.
        const observer = new ResizeObserver(() => {
            setWelcomeHeight(el.getBoundingClientRect().height);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    const welcomeOpen = messages.length === 0 && !keyboardOpen;

    /** A tool approval is waiting on the user — the composer is locked until they Approve/Deny. */
    const hasPendingApproval = pendingInputRequests.some(isApprovalRequest);
    /** Carl asked a question (e.g. "want to tweak the wording?") — typing answers it. */
    const hasPendingQuestion = !hasPendingApproval && pendingInputRequests.length > 0;

    const handleSend = async () => {
        if (!input.trim() || hasPendingApproval) return;
        const nextMessage = input;
        setInput('');
        // Embed: dismiss the keyboard on send so the layout animates back and
        // the user watches the thread start streaming.
        if (isEmbedded) textareaRef.current?.blur();
        await sendMessage(nextMessage);
    };

    return (
        <Box
            // Desktop compensates for the app shell's p={4} + topbar; the
            // embed owns the whole (native-resized) viewport, so 100% height
            // tracks the keyboard and no negative margins are needed.
            w={isEmbedded ? '100%' : 'calc(100% + 2rem)'}
            h={isEmbedded ? '100%' : 'calc(100dvh - var(--partbay-topbar))'}
            minH={isEmbedded ? 0 : 'calc(100dvh - var(--partbay-topbar))'}
            flex={isEmbedded ? 1 : undefined}
            mx={isEmbedded ? 0 : -4}
            mt={isEmbedded ? 0 : -4}
            mb={isEmbedded ? 0 : -4}
            overflowX="hidden"
        >
            <Flex h="100%" overflow="hidden">
            {/* Vanta-style conversation rail (ENG-2169) — desktop only; the
                embed keeps its ChatGPT-style header + history drawer. */}
            {!isEmbedded && (
                <>
                    <CarlSidebar
                        threads={threads}
                        activeThreadId={activeThreadId}
                        isCollapsed={isSidebarCollapsed}
                        onToggleCollapsed={toggleSidebarCollapsed}
                        onNewChat={handleStartNewThread}
                        onOpenSearch={() => setIsSearchOpen(true)}
                        onSelectThread={handleSelectThread}
                        onDeleteThread={deleteThread}
                        onRenameThread={renameThread}
                    />
                    <CarlSearchModal
                        isOpen={isSearchOpen}
                        onClose={() => setIsSearchOpen(false)}
                        threads={threads}
                        activeThreadId={activeThreadId}
                        onSelectThread={handleSelectThread}
                        onNewChat={handleStartNewThread}
                    />
                </>
            )}
            <Box
                flex={1}
                minW={0}
                h="100%"
                bg="gray.50"
                display="flex"
                flexDirection="column"
                overflow="hidden"
                position="relative"
                // Embed: give the keyboard its room so the composer (layout
                // bottom) lands right above the keys.
                pb={isEmbedded ? `${keyboardInset}px` : undefined}
                transition={isEmbedded ? 'padding-bottom 0.25s ease' : undefined}
            >
                {/* ChatGPT-style mobile header (embed only): hamburger opens the
                    searchable history drawer, compose icon starts a new chat.
                    The thread name is intentionally hidden on mobile. Tap targets
                    are 48px per Apple HIG (44pt minimum). */}
                {isEmbedded && (
                    <>
                        <Flex
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            zIndex={5}
                            align="center"
                            justify="space-between"
                            px={3}
                            py={2}
                        >
                            <IconButton
                                aria-label="Thread history"
                                icon={<Icon as={TbMenu2 as React.ElementType} boxSize={5} />}
                                onClick={() => setIsHistoryOpen(true)}
                                bg="#0071E3"
                                color="white"
                                borderRadius="full"
                                minW="40px"
                                h="40px"
                                _hover={{ bg: '#3544a0' }}
                                _active={{ bg: '#2c3888' }}
                            />
                            <IconButton
                                aria-label="New chat"
                                icon={<Icon as={TbEdit as React.ElementType} boxSize={5} />}
                                onClick={handleStartNewThread}
                                bg="#0071E3"
                                color="white"
                                borderRadius="full"
                                minW="40px"
                                h="40px"
                                _hover={{ bg: '#3544a0' }}
                                _active={{ bg: '#2c3888' }}
                            />
                        </Flex>

                        {/* Searchable thread history drawer. autoFocus off so opening
                            it does NOT pop the keyboard — the user taps search to type. */}
                        <Drawer
                            isOpen={isHistoryOpen}
                            placement="left"
                            onClose={() => setIsHistoryOpen(false)}
                            autoFocus={false}
                        >
                            <DrawerOverlay />
                            {/* `inset` keeps the 85vw width on phones (see index.css) so the
                                uncovered overlay strip stays tappable to dismiss. */}
                            <DrawerContent data-partbay-drawer="inset" maxW="85vw" bg="white">
                                <DrawerBody p={0} display="flex" flexDirection="column">
                                    {/* Explicit close: the overlay sliver is the other way out,
                                        but it is a thin target and the hamburger sits underneath
                                        the panel — without this a mis-sized drawer traps the user. */}
                                    <HStack px={3} pt={4} pb={2} spacing={2}>
                                        <InputGroup size="md">
                                            <InputLeftElement pointerEvents="none">
                                                <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={4} />
                                            </InputLeftElement>
                                            <Input
                                                value={historyQuery}
                                                onChange={(event) => setHistoryQuery(event.target.value)}
                                                placeholder="Search chats"
                                                bg="white"
                                                borderColor="gray.200"
                                                borderRadius="xl"
                                                fontSize="touchInput"
                                                _focus={{ borderColor: '#0071E3', boxShadow: 'none' }}
                                            />
                                        </InputGroup>
                                        <YaxleIconButton
                                            aria-label="Close thread history"
                                            icon={<YaxleIcon name="x" size="sm" />}
                                            onClick={() => setIsHistoryOpen(false)}
                                            variant="ghostGray"
                                            borderRadius="full"
                                            flexShrink={0}
                                        />
                                    </HStack>
                                    <Box flex={1} overflowY="auto" px={2} pb={4}>
                                        {filteredThreads.length === 0 && (
                                            <Text fontSize="sm" color="gray.500" textAlign="center" pt={8}>
                                                {historyQuery.trim() ? 'No chats match your search.' : 'No conversations yet.'}
                                            </Text>
                                        )}
                                        <VStack spacing={0.5} align="stretch">
                                            {filteredThreads.map((thread) => (
                                                <Flex
                                                    key={thread.threadId}
                                                    role="group"
                                                    align="center"
                                                    gap={2}
                                                    px={3}
                                                    py={2.5}
                                                    borderRadius="lg"
                                                    cursor="pointer"
                                                    bg={thread.threadId === activeThreadId ? 'blue.50' : 'transparent'}
                                                    _hover={{ bg: thread.threadId === activeThreadId ? 'blue.50' : 'gray.100' }}
                                                    onClick={() => {
                                                        handleSelectThread(thread.threadId);
                                                        setIsHistoryOpen(false);
                                                    }}
                                                >
                                                    <Box flex={1} minW={0}>
                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight={thread.threadId === activeThreadId ? 'semibold' : 'normal'}
                                                            color="gray.800"
                                                            whiteSpace="nowrap"
                                                            overflow="hidden"
                                                            textOverflow="ellipsis"
                                                        >
                                                            {thread.title}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.400">
                                                            {new Date(thread.updatedAt).toLocaleDateString(undefined, {
                                                                month: 'short',
                                                                day: 'numeric',
                                                            })}
                                                        </Text>
                                                    </Box>
                                                    <IconButton
                                                        aria-label={`Delete ${thread.title}`}
                                                        icon={<Icon as={TbTrash as React.ElementType} boxSize={4} />}
                                                        size="sm"
                                                        variant="ghost"
                                                        color="gray.400"
                                                        _hover={{ color: 'red.500', bg: 'red.50' }}
                                                        flexShrink={0}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            deleteThread(thread.threadId);
                                                        }}
                                                    />
                                                </Flex>
                                            ))}
                                        </VStack>
                                    </Box>
                                </DrawerBody>
                            </DrawerContent>
                        </Drawer>
                    </>
                )}

                {/* Active thread title (editable) + order-references timeline beneath it — desktop.
                    New chat / history moved into the sidebar (ENG-2169). */}
                {!isEmbedded && messages.length > 0 && activeThread && (
                    <VStack
                        position="absolute"
                        top={4}
                        left={5}
                        zIndex={5}
                        maxW="55%"
                        align="flex-start"
                        spacing={1}
                    >
                        {isEditingTitle ? (
                            <Input
                                value={titleDraft}
                                onChange={(event) => setTitleDraft(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') commitTitleEdit();
                                    if (event.key === 'Escape') setIsEditingTitle(false);
                                }}
                                onBlur={commitTitleEdit}
                                autoFocus
                                size="sm"
                                color="gray.900"
                                bg="white"
                                border="1px solid"
                                borderColor="#0071E3"
                                borderRadius="lg"
                                minW="280px"
                            />
                        ) : (
                            <HStack
                                spacing={1.5}
                                role="group"
                                cursor="pointer"
                                onClick={() => {
                                    setTitleDraft(activeThread.title);
                                    setIsEditingTitle(true);
                                }}
                                px={2.5}
                                py={1.5}
                                borderRadius="lg"
                                _hover={{ bg: 'gray.100' }}
                                transition="background 0.15s"
                                maxW="100%"
                            >
                                <Text
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color="gray.800"
                                    whiteSpace="nowrap"
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                >
                                    {activeThread.title}
                                </Text>
                                <Icon
                                    as={TbPencil as React.ElementType}
                                    boxSize={3.5}
                                    color="gray.400"
                                    opacity={0}
                                    _groupHover={{ opacity: 1 }}
                                    transition="opacity 0.15s"
                                    flexShrink={0}
                                />
                            </HStack>
                        )}

                        {/* Order references: only relevant when the thread touches more
                            than one order. The label is a hover trigger with a chevron;
                            hovering reveals a dropdown Activity-style timeline. A row click
                            jumps to the message, and its external-link action opens the
                            order detail page in a new tab. */}
                        {referencedOrders.length > 1 && (
                            <Box
                                position="relative"
                                ml={2.5}
                                mt={0.5}
                                display="inline-block"
                                onMouseEnter={() => setRefsMenuOpen(true)}
                                onMouseLeave={() => setRefsMenuOpen(false)}
                            >
                                <HStack
                                    as="button"
                                    type="button"
                                    spacing={1}
                                    px={1.5}
                                    py={1}
                                    mx={-1.5}
                                    borderRadius="md"
                                    cursor="default"
                                    bg={refsMenuOpen ? 'gray.100' : 'transparent'}
                                    transition="background 0.12s"
                                >
                                    <Text
                                        fontSize="xs"
                                        fontWeight="semibold"
                                        color="gray.500"
                                        letterSpacing="0.02em"
                                    >
                                        Order references
                                    </Text>
                                    <Icon
                                        as={TbChevronDown as React.ElementType}
                                        boxSize={3.5}
                                        color={refsMenuOpen ? '#0071E3' : 'gray.400'}
                                        transform={refsMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                                        transition="transform 0.15s ease, color 0.15s"
                                    />
                                </HStack>

                                {/* Dropdown timeline — revealed on hover of the trigger */}
                                <Box
                                    position="absolute"
                                    top="100%"
                                    left={0}
                                    mt={1}
                                    minW="220px"
                                    maxW="360px"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="xl"
                                    boxShadow="none"
                                    p={2}
                                    zIndex={20}
                                    opacity={refsMenuOpen ? 1 : 0}
                                    visibility={refsMenuOpen ? 'visible' : 'hidden'}
                                    transform={refsMenuOpen ? 'translateY(0)' : 'translateY(-4px)'}
                                    transition="opacity 0.15s ease, transform 0.15s ease, visibility 0.15s"
                                >
                                    <Box position="relative" maxH="240px" overflowY="auto">
                                        <Box
                                            position="absolute"
                                            left="9px"
                                            top="14px"
                                            bottom="14px"
                                            w="1px"
                                            bg="gray.200"
                                            pointerEvents="none"
                                        />
                                        <VStack spacing={0} align="stretch">
                                            {referencedOrders.map((order) => {
                                                const label = order.roNumber
                                                    ? `RO ${order.roNumber}`
                                                    : (order.vin ?? 'Order');
                                                const subtitle = order.vehicleName
                                                    || (order.roNumber && order.vin ? order.vin : undefined);
                                                return (
                                                    <HStack
                                                        key={order.orderId}
                                                        role="group"
                                                        spacing={1.5}
                                                        py={1.5}
                                                        pl={1.5}
                                                        pr={0.5}
                                                        borderRadius="md"
                                                        cursor="pointer"
                                                        _hover={{ bg: 'gray.100' }}
                                                        transition="background 0.12s"
                                                        onClick={() => scrollToMessage(order.messageId)}
                                                    >
                                                        <Flex
                                                            w="18px"
                                                            h="18px"
                                                            align="center"
                                                            justify="center"
                                                            borderRadius="full"
                                                            bg="white"
                                                            border="1.5px solid"
                                                            borderColor="gray.300"
                                                            flexShrink={0}
                                                            zIndex={1}
                                                            _groupHover={{ borderColor: '#0071E3' }}
                                                            transition="border-color 0.12s"
                                                        >
                                                            <Icon
                                                                as={TbPackage as React.ElementType}
                                                                boxSize={2.5}
                                                                color="gray.500"
                                                                _groupHover={{ color: '#0071E3' }}
                                                            />
                                                        </Flex>
                                                        <Text
                                                            fontSize="xs"
                                                            color="gray.600"
                                                            whiteSpace="nowrap"
                                                            lineHeight="1.35"
                                                            flex={1}
                                                        >
                                                            <Text as="span" fontWeight="medium" color="gray.800">
                                                                {label}
                                                            </Text>
                                                            {subtitle ? (
                                                                <>
                                                                    <Text as="span" color="gray.400"> · </Text>
                                                                    <Text as="span">{subtitle}</Text>
                                                                </>
                                                            ) : null}
                                                        </Text>
                                                        <IconButton
                                                            aria-label={`Open ${label} in new tab`}
                                                            icon={<Icon as={TbExternalLink as React.ElementType} boxSize={3.5} />}
                                                            size="xs"
                                                            variant="ghost"
                                                            color="gray.400"
                                                            opacity={0}
                                                            minW="22px"
                                                            h="22px"
                                                            _groupHover={{ opacity: 1 }}
                                                            _hover={{ color: '#0071E3', bg: 'blue.50' }}
                                                            flexShrink={0}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                window.open(`/order/${order.orderId}`, '_blank');
                                                            }}
                                                        />
                                                    </HStack>
                                                );
                                            })}
                                        </VStack>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </VStack>
                )}

                {/* Claude-style conversation body: when the thread is empty the composer
                    is centered directly under the logo + greeting. On the first message the
                    greeting collapses and the composer glides down to the footer while the
                    message list fills the space above it. */}
                <Flex direction="column" flex={1} minH={0} position="relative" zIndex={1}>
                    {/* Messages / top region (also acts as the top spacer while empty) */}
                    <Box flex={1} minH={0} position="relative" overflow="hidden">
                        {messages.length > 0 && (
                            <>
                                <Flex h="100%" overflowY="auto" px={{ base: 4, md: 6 }}>
                                    <VStack
                                        w="100%"
                                        spacing={5}
                                        align="stretch"
                                        // Embed has one compact header row; desktop clears the
                                        // corner controls (+ order-references when present).
                                        pt={isEmbedded
                                            ? 16
                                            : (referencedOrders.length > 1 ? 24 : 20)}
                                        pb={4}
                                        maxW={isEmbedded ? '100%' : '70vw'}
                                        mx="auto"
                                    >
                                        {messages.map((message, messageIndex) => {
                                            const isUser = message.role === 'user';

                                            // Claude-style: user messages are a right-aligned pill; assistant
                                            // content renders plain (its own cards provide framing) full-width.
                                            if (isUser) {
                                                return (
                                                    <Flex key={message.id} justify="flex-end" animation={`${fadeIn} 0.3s ease-out`}>
                                                        <Box
                                                            bg="#0071E3"
                                                            color="white"
                                                            borderRadius="2xl"
                                                            borderTopRightRadius="md"
                                                            px={4}
                                                            py={2.5}
                                                            maxW="85%"
                                                        >
                                                            <CarlMessageBody message={message} />
                                                        </Box>
                                                    </Flex>
                                                );
                                            }
                                            const isStreamingThis = isLoading && messageIndex === messages.length - 1;
                                            // The turn isn't done while it's streaming OR parked on an
                                            // approval/question — no done-state UI (thumbs) until then.
                                            const isSettled = !isStreamingThis
                                                && (messageIndex !== messages.length - 1 || pendingInputRequests.length === 0);
                                            return (
                                                <Box
                                                    key={message.id}
                                                    id={`carl-message-${message.id}`}
                                                    w="100%"
                                                    color="gray.800"
                                                    animation={`${fadeIn} 0.3s ease-out`}
                                                >
                                                    <CarlMessageBody
                                                        message={message}
                                                        isStreaming={isStreamingThis}
                                                    />
                                                    {isSettled && message.content.trim() && (
                                                        <CarlFeedbackControls
                                                            message={message}
                                                            onSubmit={submitFeedback}
                                                            showNudge={nudgedMessageId === message.id}
                                                        />
                                                    )}
                                                </Box>
                                            );
                                        })}

                                        {pendingInputRequests.map((request) => (
                                            <PendingInputCard
                                                key={request.requestId}
                                                request={request}
                                                isLoading={isLoading}
                                                onRespond={respondToInput}
                                            />
                                        ))}

                                        {isLoading && messages[messages.length - 1]?.role === 'user' && <ThinkingIndicator />}
                                        {/* Spacer so the end of a response scrolls clear above the input */}
                                        <Box flexShrink={0} h="160px" aria-hidden />
                                        <div ref={endRef} />
                                    </VStack>
                                </Flex>

                                {/* Fade overlays */}
                                <Box
                                    pointerEvents="none"
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    h="60px"
                                    bgGradient="linear(to-b, gray.50, transparent)"
                                />
                                <Box
                                    pointerEvents="none"
                                    position="absolute"
                                    bottom={0}
                                    left={0}
                                    right={0}
                                    h="60px"
                                    bgGradient="linear(to-t, gray.50, transparent)"
                                />
                            </>
                        )}
                    </Box>

                    {/* Logo + greeting: shown only on a fresh thread, collapses on first
                        message — and, in the embed, while the keyboard is up, so the
                        composer can drop to sit right above the keys. */}
                    <Box
                        overflow="hidden"
                        maxH={welcomeOpen ? `${welcomeHeight ?? 400}px` : '0px'}
                        opacity={welcomeOpen ? 1 : 0}
                        pointerEvents={welcomeOpen ? 'auto' : 'none'}
                    >
                        <VStack ref={welcomeRef} spacing={4} pb={6} px={4}>
                            <CarlLogo size="120px" parallax welcome />
                            <VStack spacing={1}>
                                <Text
                                    fontSize={{ base: '2xl', md: '3xl' }}
                                    fontWeight="semibold"
                                    color="gray.900"
                                    textAlign="center"
                                >
                                    {getGreeting()}, {userDisplayName}
                                </Text>
                                <Text
                                    fontSize="md"
                                    color="gray.500"
                                    textAlign="center"
                                >
                                    How can I help you with parts today?
                                </Text>
                            </VStack>
                        </VStack>
                    </Box>

                    {/* Composer: a single persistent node so it animates smoothly between
                        the centered (empty) and footer (active) positions. */}
                    <Box
                        px={{ base: 4, md: 6 }}
                        pt={messages.length === 0 ? 0 : 3}
                        pb={4}
                    >
                        <Box
                            maxW={isEmbedded ? '100%' : (messages.length === 0 ? '55vw' : '70vw')}
                            w="100%"
                            mx="auto"
                        >
                            <Box
                                position="relative"
                                bg="white"
                                borderRadius="2xl"
                                border="1px solid"
                                borderColor="gray.200"
                                boxShadow="none"
                                p={1}
                                transition="border-color 0.2s, box-shadow 0.2s"
                                _focusWithin={{
                                    borderColor: '#0071E3',
                                    boxShadow: '0 0 0 3px rgba(0, 113, 227, 0.12)',
                                }}
                            >
                                <Textarea
                                    ref={textareaRef}
                                    value={input}
                                    onChange={(event) => setInput(event.target.value)}
                                    onKeyDown={(event) => {
                                        // Embed (mobile): Enter inserts a newline, ChatGPT-style —
                                        // sending is exclusively the blue button. Desktop keeps
                                        // Enter-to-send (Shift+Enter for a newline).
                                        if (!isEmbedded && event.key === 'Enter' && !event.shiftKey) {
                                            event.preventDefault();
                                            handleSend();
                                        }
                                    }}
                                    placeholder={hasPendingApproval
                                        ? 'Approve or deny the request above to continue…'
                                        : hasPendingQuestion
                                            ? "Reply to Carl's question above…"
                                            // Embed: two lines while idle; short one-liner
                                            // once the keyboard compacts the composer.
                                            : keyboardOpen
                                                ? 'Ask me anything'
                                                : isEmbedded
                                                    ? brandCopy("Hi, I'm Carl, Yaxle's AI assistant.\nAsk me anything")
                                                    : brandCopy("Hi, I'm Carl, Yaxle's AI assistant. Ask me anything")}
                                    color="gray.900"
                                    _placeholder={{ color: 'gray.400' }}
                                    bg="transparent"
                                    border="none"
                                    borderRadius="xl"
                                    resize="none"
                                    rows={keyboardOpen ? 1 : (isEmbedded ? 2 : undefined)}
                                    // Embed: a taller two-line box while idle; a single
                                    // ChatGPT-style row (still grows with content) once
                                    // the keyboard is up.
                                    minH={isEmbedded
                                        ? (keyboardOpen ? '44px' : '92px')
                                        : (messages.length === 0 ? '120px' : '56px')}
                                    maxH="200px"
                                    px={4}
                                    py={isEmbedded ? (keyboardOpen ? 2.5 : 3) : 3}
                                    pr={isEmbedded ? 14 : 4}
                                    _focus={{ boxShadow: 'none' }}
                                    // Embed: `md` is 1rem, which the fluid html root renders
                                    // under iOS's 16px auto-zoom threshold on a phone. The
                                    // touchInput token is the fixed-px safe size (typography.ts).
                                    fontSize={isEmbedded ? 'touchInput' : 'md'}
                                    transition={isEmbedded ? 'min-height 0.25s ease' : undefined}
                                    isDisabled={hasPendingApproval}
                                />
                                {isEmbedded ? (
                                    // Mobile: ChatGPT-style — a blue send button appears once the
                                    // user types (nothing while empty). Enter inserts a newline, so
                                    // this is the only way to send. The button acts on touchend with
                                    // preventDefault: a plain click never lands here because the tap
                                    // first blurs the textarea, the keyboard dismissal shifts the
                                    // layout, and the click fires against the moved page.
                                    !!input.trim() && (
                                        <IconButton
                                            aria-label="Send message"
                                            icon={<Icon as={TbArrowUp as React.ElementType} boxSize="18px" />}
                                            position="absolute"
                                            right={2}
                                            bottom={2}
                                            zIndex={1}
                                            // Keep the textarea focused through the press so the
                                            // keyboard can't start dismissing mid-tap.
                                            onMouseDown={(event) => event.preventDefault()}
                                            onTouchEnd={(event) => {
                                                event.preventDefault();
                                                handleSend();
                                            }}
                                            // Fallback for non-touch input (e.g. testing in a
                                            // desktop browser); preventDefault above suppresses
                                            // the synthetic click on touch, so no double-send.
                                            onClick={handleSend}
                                            isDisabled={hasPendingApproval}
                                            size="sm"
                                            borderRadius="full"
                                            bg="#0071E3"
                                            color="white"
                                            _hover={{ bg: '#3544a0' }}
                                            _active={{ bg: '#2c3888' }}
                                            minW="32px"
                                            h="32px"
                                        />
                                    )
                                ) : (
                                    <Flex justify="flex-end" px={2} pb={2}>
                                        <IconButton
                                            aria-label="Send message"
                                            icon={<Icon as={TbArrowUp as React.ElementType} w={4} h={4} />}
                                            onClick={handleSend}
                                            isDisabled={!input.trim() || isLoading || hasPendingApproval}
                                            size="sm"
                                            borderRadius="full"
                                            bg="#0071E3"
                                            color="white"
                                            _hover={{ bg: '#3544a0' }}
                                            _disabled={{
                                                bg: '#0071E3',
                                                opacity: 0.6,
                                                cursor: 'not-allowed',
                                            }}
                                            minW="28px"
                                            h="28px"
                                        />
                                    </Flex>
                                )}
                            </Box>
                            {/* AI disclaimer — only once a thread is under way, mirroring
                                the pattern on assistants like Claude/ChatGPT. */}
                            {messages.length > 0 && (
                                <Text
                                    mt={2}
                                    fontSize="xs"
                                    color="gray.400"
                                    textAlign="center"
                                >
                                    Carl is AI and can make mistakes. Please double-check responses.
                                </Text>
                            )}
                        </Box>
                    </Box>

                    {/* Bottom spacer: flex-grow animates 1 -> 0 so the composer glides
                        from centered (empty) to the footer once the thread starts —
                        or, in the embed, the moment the keyboard comes up. */}
                    <Box
                        flexGrow={messages.length === 0 && !keyboardOpen ? 1 : 0}
                        transition="flex-grow 0.5s ease"
                        aria-hidden
                    />
                </Flex>
            </Box>
            </Flex>
        </Box>
    );
};

export default AskCarl;
